import { Input } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import type { PowerlistCompany } from "../../api/powerlist.service";
import styles from "../../components/CompanyPicker/CompanyPicker.module.css";

const SEARCH_DEBOUNCE_MS = 300;

type PowerlistCompanyPickerProps = {
  label: string;
  listboxId: string;
  placeholder: string;
  search: (query: string) => Promise<PowerlistCompany[]>;
  value: PowerlistCompany | null;
  query: string;
  onQueryChange: (query: string) => void;
  onChange: (company: PowerlistCompany) => void;
  error?: string;
  disabled?: boolean;
};

export function PowerlistCompanyPicker({
  label,
  listboxId,
  placeholder,
  search,
  value,
  query,
  onQueryChange,
  onChange,
  error,
  disabled,
}: PowerlistCompanyPickerProps) {
  const [results, setResults] = useState<PowerlistCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const selectedName = value?.name.trim() ?? null;

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setResults([]);
    setSearchError(null);
    const term = query.trim();
    if (term.length === 0 || term === selectedName) {
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const rows = await search(term);
        if (requestId !== requestIdRef.current) return;
        setResults(rows);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setResults([]);
        setSearchError("Couldn't load companies. Try again.");
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  }, [query, selectedName, search]);

  function handleInputChange(next: string) {
    onQueryChange(next);
    setIsOpen(true);
  }

  function handleSelect(company: PowerlistCompany) {
    onChange(company);
    setResults([]);
    setIsOpen(false);
  }

  const term = query.trim();
  const showDropdown = isOpen && term.length > 0 && term !== selectedName;

  return (
    <div className={styles.picker} ref={containerRef}>
      <Input
        label={label}
        value={query}
        onChange={event => handleInputChange(event.target.value)}
        onFocus={() => setIsOpen(true)}
        error={error}
        disabled={disabled}
        fullWidth
        autoComplete="off"
        placeholder={placeholder}
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        aria-controls={listboxId}
      />
      {showDropdown && (
        <ul id={listboxId} role="listbox" className={styles.dropdown}>
          {loading && (
            <li className={styles.dropdownStatus} aria-live="polite">
              Searching…
            </li>
          )}
          {!loading && searchError && (
            <li className={styles.dropdownError} role="alert">
              {searchError}
            </li>
          )}
          {!loading && !searchError && results.length === 0 && (
            <li className={styles.dropdownStatus} aria-live="polite">
              No companies found
            </li>
          )}
          {!loading &&
            !searchError &&
            results.map(company => (
              <li
                key={company.id}
                role="option"
                aria-selected={value?.id === company.id}
                tabIndex={0}
                className={styles.dropdownItem}
                onClick={() => handleSelect(company)}
                onKeyDown={event => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleSelect(company);
                  }
                }}
              >
                {company.name}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
